/**
 * Settings → Notifications (DOP-274 round 2).
 *
 * Live testing showed that "we asked for permission" is nowhere near enough.
 * Chrome's quieter-notifications UI swallowed the prompt, so the permission sat
 * at "default" and the app looked broken; and once the site permission WAS
 * granted, still nothing appeared until Google Chrome itself was allowed to
 * notify in macOS System Settings. Both of those are outside the page's reach,
 * so the page's job is to say plainly which step is missing.
 *
 * Hence a state machine, not a switch: what this section shows is decided by
 * the browser permission, and only the "granted" branch is a preference at all.
 */

import { Bell, BellOff, ChevronDown, ChevronRight } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

import {
	desktopNotificationPermission,
	isDesktopNotificationSupported,
	requestDesktopNotificationPermission,
	showDesktopNotification,
} from "../../utils/desktopNotification";
import { playNotificationChime } from "../../utils/notificationChime";
import {
	isDesktopNotificationsEnabled,
	isNotificationSoundEnabled,
	setDesktopNotificationsEnabled,
	setNotificationSoundEnabled,
} from "../../utils/notificationPreferences";
import SettingsSection from "./SettingsSection";

/** macOS is the only OS whose steps we can name exactly, so it gets them. */
const isMacOs = (): boolean => {
	if (typeof navigator === "undefined") return false;
	return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent || "");
};

interface ToggleRowProps {
	label: string;
	description: string;
	checked: boolean;
	disabled?: boolean;
	onChange: (checked: boolean) => void;
}

/** A labelled checkbox row. No switch component exists in this codebase yet. */
const ToggleRow: React.FC<ToggleRowProps> = ({
	label,
	description,
	checked,
	disabled,
	onChange,
}) => (
	<label className="flex items-start gap-3 py-2 cursor-pointer">
		<input
			type="checkbox"
			checked={checked}
			disabled={disabled}
			onChange={(event) => onChange(event.target.checked)}
			className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#008782] focus:ring-[#008782] disabled:opacity-50"
		/>
		<span>
			<span className="block text-base font-medium text-text-base">
				{label}
			</span>
			<span className="block text-sm text-text-muted">{description}</span>
		</span>
	</label>
);

/**
 * "Not seeing notifications?" — the macOS gate that cost us a live session.
 *
 * Collapsed by default: it is troubleshooting, not setup, and it is wrong for
 * anyone whose notifications already work.
 */
const TroubleshootingPanel: React.FC = () => {
	const [open, setOpen] = useState(false);
	const Chevron = open ? ChevronDown : ChevronRight;
	return (
		<div className="mt-4 border-t border-gray-100 pt-3">
			<button
				type="button"
				onClick={() => setOpen((current) => !current)}
				aria-expanded={open}
				className="flex items-center gap-1 text-sm font-medium text-text-base hover:text-[#008782]"
			>
				<Chevron className="w-4 h-4" />
				<span>Not seeing notifications?</span>
			</button>
			{open &&
				(isMacOs() ? (
					<div className="mt-2 text-sm text-text-muted">
						<p>
							macOS decides whether your browser may show banners at all — the
							site permission above is not enough on its own.
						</p>
						<ol className="mt-2 list-decimal space-y-1 pl-5">
							<li>Open System Settings → Notifications.</li>
							<li>Find Google Chrome in the list and select it.</li>
							<li>
								Turn on “Allow notifications” and choose Banners or Alerts.
							</li>
							<li>
								Turn on “Play sound for notifications” there if you want the
								system sound as well as Doppler’s chime.
							</li>
						</ol>
						<p className="mt-2">
							Also check that Focus or Do Not Disturb is off.
						</p>
					</div>
				) : (
					<p className="mt-2 text-sm text-text-muted">
						Your operating system has its own per-app notification setting —
						allow your browser to show notifications there, and check that any
						focus or do-not-disturb mode is off.
					</p>
				))}
		</div>
	);
};

const NotificationSettings: React.FC = () => {
	const [supported] = useState(isDesktopNotificationSupported);
	const [permission, setPermission] = useState<NotificationPermission | null>(
		desktopNotificationPermission,
	);
	const [desktopEnabled, setDesktopEnabled] = useState(
		isDesktopNotificationsEnabled,
	);
	const [soundEnabled, setSoundEnabled] = useState(isNotificationSoundEnabled);
	/** What the last test actually sent, so the confirmation cannot overclaim. */
	const [testSent, setTestSent] = useState<{
		banner: boolean;
		chime: boolean;
	} | null>(null);

	// The answer can change outside this page — in another tab, or in Chrome's
	// own site settings — so re-read it when the user comes back to this one.
	useEffect(() => {
		const sync = () => setPermission(desktopNotificationPermission());
		document.addEventListener("visibilitychange", sync);
		return () => document.removeEventListener("visibilitychange", sync);
	}, []);

	const handleEnable = useCallback(() => {
		void requestDesktopNotificationPermission().then((next) => {
			setPermission(next);
			// Asking for it IS opting in; an off preference here would be a switch
			// the user never touched silently cancelling the click they just made.
			if (next === "granted") {
				setDesktopNotificationsEnabled(true);
				setDesktopEnabled(true);
			}
		});
	}, []);

	const handleDesktopToggle = useCallback((checked: boolean) => {
		setDesktopNotificationsEnabled(checked);
		setDesktopEnabled(checked);
		// The confirmation below describes a test run under the old settings.
		setTestSent(null);
	}, []);

	const handleSoundToggle = useCallback((checked: boolean) => {
		setNotificationSoundEnabled(checked);
		setSoundEnabled(checked);
		setTestSent(null);
	}, []);

	const handleTest = useCallback(() => {
		// Deliberately bypasses the tab-hidden rule that governs real run
		// announcements: the whole point is to see it while looking at this page.
		// It does NOT bypass the preferences — a test that fires a banner the
		// user just turned off is testing something they did not ask for, and
		// tells them nothing about what a real extraction will do.
		if (desktopEnabled) {
			showDesktopNotification(
				"Doppler — test notification",
				"If you can see this, notifications are working.",
			);
		}
		if (soundEnabled) playNotificationChime();
		setTestSent({ banner: desktopEnabled, chime: soundEnabled });
	}, [desktopEnabled, soundEnabled]);

	if (!supported) {
		return (
			<SettingsSection
				title="Notifications"
				description="Get told when a long extraction finishes."
			>
				<p className="text-sm text-text-muted">
					This browser does not support desktop notifications. Doppler will
					still show the result in the app when you come back to it.
				</p>
			</SettingsSection>
		);
	}

	return (
		<SettingsSection
			title="Notifications"
			description="A KPI extraction takes minutes. Doppler can tell your desktop when one finishes, so you do not have to sit and watch it."
		>
			{permission === "default" && (
				<div>
					<p className="text-sm text-text-muted">
						Notifications are not turned on for Doppler yet. Your browser will
						ask you to confirm.
					</p>
					<button
						type="button"
						onClick={handleEnable}
						className="mt-3 flex items-center gap-2 px-4 py-2 bg-[#008782] text-white rounded-lg text-sm font-medium hover:bg-[#004C4B] transition-colors"
					>
						<Bell className="w-4 h-4" />
						<span>Enable notifications</span>
					</button>
				</div>
			)}

			{permission === "denied" && (
				<div className="text-sm text-text-muted">
					<p className="flex items-center gap-2 text-text-base">
						<BellOff className="w-4 h-4" />
						<span>Your browser is blocking notifications for Doppler.</span>
					</p>
					<p className="mt-2">
						The browser will not ask again, so this one has to be changed by
						hand:
					</p>
					<ol className="mt-2 list-decimal space-y-1 pl-5">
						<li>
							Click the lock or sliders icon to the left of the address bar.
						</li>
						<li>Open “Site settings”.</li>
						<li>Set Notifications to “Allow”.</li>
						<li>Reload this page.</li>
					</ol>
				</div>
			)}

			{permission === "granted" && (
				<div>
					<ToggleRow
						label="Desktop notifications"
						description="Show a banner when an extraction finishes while you are in another tab or app."
						checked={desktopEnabled}
						onChange={handleDesktopToggle}
					/>
					{/*
					 * Sound stands on its own. It used to be disabled unless the banner
					 * was on, which read as a bug — you could not tick it first — and was
					 * wrong besides: the chime needs no permission from the browser or
					 * the OS, so it is exactly what still works when banners are blocked.
					 */}
					<ToggleRow
						label="Sound"
						description="Play a short chime when an extraction finishes. Your operating system may play its own sound as well."
						checked={soundEnabled}
						onChange={handleSoundToggle}
					/>
					<button
						type="button"
						onClick={handleTest}
						disabled={!desktopEnabled && !soundEnabled}
						className="mt-3 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
					>
						Send test notification
					</button>
					{!desktopEnabled && !soundEnabled && (
						<p className="mt-2 text-sm text-text-muted">
							Both are off, so there is nothing to test — a real extraction will
							finish quietly and wait for you in the app.
						</p>
					)}
					{testSent?.banner && (
						<p className="mt-2 text-sm text-text-muted">
							Sent. If nothing appeared, your operating system is blocking your
							browser — see below.
						</p>
					)}
					{testSent && !testSent.banner && testSent.chime && (
						<p className="mt-2 text-sm text-text-muted">
							Played the chime. Desktop notifications are off, so no banner was
							sent. If you heard nothing, check this tab is not muted.
						</p>
					)}
				</div>
			)}

			<TroubleshootingPanel />
		</SettingsSection>
	);
};

export default NotificationSettings;
